// src/hooks/useExpedienteDetallado.js
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { differenceInYears } from 'date-fns';
import api from '../services/api';
import { useToast } from '../context/ToastContext';
import { useBovedaSegura } from './useBovedaSegura';
import { useAuth } from '../context/AuthContext';

export function useExpedienteDetallado(pacienteId, onBack) {
    const { user } = useAuth();
    const { showToast } = useToast();
    const navigate = useNavigate();
    const { obtenerUrlDocumento, procesando: bovedaCargando } = useBovedaSegura();

    // Estados de datos del expediente
    const [paciente, setPaciente] = useState(null);
    const [consultas, setConsultas] = useState([]);
    const [consultaSeleccionada, setConsultaSeleccionada] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showSkeleton, setShowSkeleton] = useState(false);

    // Estados de navegación interna y modales
    const [activeTab, setActiveTab] = useState('evolutivo');
    const [openInforme, setOpenInforme] = useState(false);
    const [openNuevaConsulta, setOpenNuevaConsulta] = useState(false);

    const [visorDoc, setVisorDoc] = useState({
        isOpen: false,
        blobUrl: null,
        mimetype: null,
        cargando: false
    });

    // 1. CARGA PARALELA ATÓMICA DE DATOS CLINICOS
    const fetchDatosExpediente = useCallback(async () => {
        setLoading(true);
        try {
            const [resPac, resCon] = await Promise.all([
                api.get(`/pacientes/${pacienteId}`),
                api.get(`/consultas/paciente/${pacienteId}`)
            ]);

            setPaciente(resPac);

            const datosConsultas = Array.isArray(resCon) ? resCon : [];
            const ordenadas = [...datosConsultas].sort((a, b) => new Date(b.fecha) - new Date(a.fecha));
            setConsultas(ordenadas);
        } catch (err) {
            showToast('Error al recuperar la ficha clínica en el servidor.', 'error');
            if (onBack) onBack();
        } finally {
            setLoading(false);
        }
    }, [pacienteId, onBack, showToast]);

    useEffect(() => {
        if (pacienteId) fetchDatosExpediente();
    }, [pacienteId, fetchDatosExpediente]);

    // 2. ESCUDO ANTI-PARPADEO (Caché rápida)
    useEffect(() => {
        let timer;
        if (loading) {
            timer = setTimeout(() => setShowSkeleton(true), 200);
        } else {
            setShowSkeleton(false);
        }
        return () => clearTimeout(timer);
    }, [loading]);

    // 3. ATENDER AHORA (visita sin cita previa / walk-in)
    // Abre una consulta en el momento y lleva al médico a la Estación Clínica.
    // El motivo "sin cita previa" permite al backend saltar la validación de
    // solapamiento de agenda (el médico decide atender ahora bajo su criterio).
    const handleAtenderAhora = async () => {
        try {
            showToast('Abriendo consulta…', 'info');
            const payload = {
                paciente: pacienteId,
                profesional: user?.id || user?._id,
                fecha: new Date().toISOString(),
                motivo: 'Atención sin cita previa'
            };

            const res = await api.post('/consultas', payload);
            const nuevaCita = res.data || res;
            navigate(`/workspace/${nuevaCita.id || nuevaCita._id}`);
        } catch (error) {
            console.error('Error al abrir la consulta:', error);
            // Si no se puede abrir al vuelo (p. ej. ya hay una activa), ofrecemos agendar.
            setOpenNuevaConsulta(true);
            showToast('No se pudo abrir la consulta directa. Programa una hora para este paciente.', 'warning');
        }
    };

    // Abre el modal para PROGRAMAR una cita futura de este paciente.
    const handleProgramarCita = () => setOpenNuevaConsulta(true);

    // ⚖️ Registrar el consentimiento informado del paciente (RGPD). Inmutable.
    const registrarConsentimiento = async () => {
        try {
            await api.post(`/pacientes/${pacienteId}/consentimiento`, {});
            showToast('Consentimiento del paciente registrado.', 'success');
            fetchDatosExpediente();
        } catch (error) {
            showToast(error.message || 'No se pudo registrar el consentimiento.', 'error');
        }
    };

    // 4. PROTOCOLO DE DESENCRIPTACIÓN EN MEMORIA RAM
    const handleAbrirVisor = async (url, e) => {
        if (e && e.preventDefault) e.preventDefault();
        if (e && e.stopPropagation) e.stopPropagation();

        setVisorDoc({ isOpen: true, blobUrl: null, mimetype: null, cargando: true });
        const resultado = await obtenerUrlDocumento(url);

        if (resultado && resultado.blobUrl) {
            setVisorDoc({
                isOpen: true,
                blobUrl: resultado.blobUrl,
                mimetype: resultado.trueMimetype,
                cargando: false
            });
        } else {
            setVisorDoc({ isOpen: false, blobUrl: null, mimetype: null, cargando: false });
            showToast('Error al desencriptar el archivo de la bóveda', 'error');
        }
    };

    const handleCerrarVisor = () => {
        if (visorDoc.blobUrl) window.URL.revokeObjectURL(visorDoc.blobUrl);
        setVisorDoc({ isOpen: false, blobUrl: null, mimetype: null, cargando: false });
    };

    const calcularEdad = (fechaNacimiento) => {
        if (!fechaNacimiento) return '--';
        return differenceInYears(new Date(), new Date(fechaNacimiento));
    };

    // 🩺 Consulta ACTIVA (en curso) de este paciente. Es la que el médico está
    // atendiendo ahora mismo; sirve para ofrecer el salto a la Estación Clínica.
    const consultaEnCurso = useMemo(
        () => consultas.find(c => c.estado === 'en_curso') || null,
        [consultas]
    );

    // Salta a la Estación Clínica (MedicalWorkspace) para actualizar la consulta actual.
    const irAEstacionClinica = useCallback((consultaId) => {
        const id = consultaId || consultaEnCurso?.id || consultaEnCurso?._id;
        if (id) navigate(`/workspace/${id}`);
        else showToast('No hay ninguna consulta en curso para este paciente.', 'info');
    }, [navigate, consultaEnCurso, showToast]);

    const BASE_URL = process.env.REACT_APP_API_URL
        ? process.env.REACT_APP_API_URL.replace('/api', '')
        : ''; // mismo origen (proxy de React en desarrollo, nginx en produccion)

    return {
        paciente,
        setPaciente, // 🚀 AÑADIDO: Exportamos la función de mutación del estado
        consultas, consultaSeleccionada, setConsultaSeleccionada,
        loading, showSkeleton, activeTab, setActiveTab,
        openInforme, setOpenInforme, openNuevaConsulta, setOpenNuevaConsulta,
        visorDoc, bovedaCargando, BASE_URL,
        handleAbrirVisor, handleCerrarVisor,
        handleAtenderAhora, handleProgramarCita,
        calcularEdad, fetchDatosExpediente,
        consultaEnCurso, irAEstacionClinica,
        registrarConsentimiento
    };
}